"use client";

import { useEffect, useRef, useState } from "react";
import { ArrowUpRight, Instagram, Linkedin } from "lucide-react";
import { Button } from "@/components/ui/button";
import { allowPreviews, usePreviewsAllowed } from "@/lib/embeds";
import { embedUrl, instagramHandle, socialKind, type SocialPlatform, type SocialPost } from "@/lib/social";
import { cn } from "@/lib/utils";

const META = {
  instagram: { label: "Instagram", Icon: Instagram, post: 620, profile: 760 },
  linkedin: { label: "LinkedIn", Icon: Linkedin, post: 560, profile: 0 },
} as const;

/**
 * One framed post or profile.
 *
 * Instagram's embed reports its own height by postMessage, which is what its embed.js
 * listens for; this listens for the same message, so a long caption is not cut off and a
 * short one leaves no gap. LinkedIn sends nothing, so it keeps a fixed height. A shimmer
 * holds the space until the frame has loaded.
 */
const Frame = ({ post, height }: { post: SocialPost; height: number }) => {
  const ref = useRef<HTMLIFrameElement>(null);
  const [loaded, setLoaded] = useState(false);
  const [measured, setMeasured] = useState<number | null>(null);
  const src = embedUrl(post);

  useEffect(() => {
    if (post.platform !== "instagram") return;
    const onMessage = (event: MessageEvent) => {
      if (event.source !== ref.current?.contentWindow) return;
      try {
        if (!/(^|\.)instagram\.com$/.test(new URL(event.origin).hostname)) return;
        const data = typeof event.data === "string" ? JSON.parse(event.data) : event.data;
        const next = Number(data?.details?.height);
        if (data?.type === "MEASURE" && next > 100) setMeasured(Math.min(next, 2000));
      } catch {
        /* Not ours. */
      }
    };
    window.addEventListener("message", onMessage);
    return () => window.removeEventListener("message", onMessage);
  }, [post.platform]);

  if (!src) return null;

  return (
    <div className="relative overflow-hidden rounded-2xl border border-black/[0.08] bg-white" style={{ height: measured ?? height }}>
      {!loaded && <div aria-hidden className="absolute inset-0 animate-pulse bg-brand-paper/70" />}
      <iframe
        ref={ref}
        src={src}
        title={`${META[post.platform].label} ${socialKind(post) === "profile" ? "profile" : "post"}`}
        loading="lazy"
        scrolling="no"
        allow="encrypted-media"
        onLoad={() => setLoaded(true)}
        className={cn("size-full transition-opacity duration-500", loaded ? "opacity-100" : "opacity-0")}
      />
    </div>
  );
};

/** A LinkedIn company or person page: LinkedIn has no embed for one, so it is a link out. */
const ProfileLink = ({ post }: { post: SocialPost }) => {
  const { label, Icon } = META[post.platform];
  const handle = post.platform === "instagram" ? instagramHandle(post.url) : post.url.split("/").filter(Boolean).pop();
  return (
    <a
      href={post.url}
      target="_blank"
      rel="noreferrer noopener"
      className="group flex items-center justify-between gap-4 rounded-2xl border border-black/[0.08] bg-white px-5 py-4 transition-colors duration-300 hover:border-brand-accent"
    >
      <span className="flex items-center gap-3">
        <Icon className="size-5 text-brand-ink/60" aria-hidden />
        <span>
          <span className="block text-sm font-semibold text-brand-ink">{handle}</span>
          <span className="block font-mono text-[10px] uppercase tracking-[0.18em] text-brand-ink/60">
            Content made for {label}
          </span>
        </span>
      </span>
      <ArrowUpRight className="size-4 text-brand-ink/60 transition-transform duration-300 group-hover:-translate-y-0.5 group-hover:translate-x-0.5 group-hover:text-brand-accent" />
    </a>
  );
};

/**
 * A client's Instagram or LinkedIn, as the platform renders it: the account grid when the
 * studio runs the whole feed, and the individual posts it made, in a grid of their own.
 */
export const SocialFeed = ({ platform, posts }: { platform: SocialPlatform; posts: SocialPost[] }) => {
  const ok = usePreviewsAllowed();
  const { label, Icon } = META[platform];
  const profiles = posts.filter((post) => socialKind(post) === "profile");
  const singles = posts.filter((post) => socialKind(post) === "post");
  const outLink = profiles[0]?.url ?? singles[0]?.url;

  if (!ok) {
    return (
      <div className="flex flex-col items-center justify-center gap-4 rounded-[20px] border border-dashed border-black/15 bg-brand-paper/40 px-6 py-14 text-center">
        <Icon className="size-7 text-brand-ink/60" aria-hidden />
        <p className="max-w-[34ch] text-sm leading-relaxed text-brand-ink/60">
          {profiles.length > 0 ? `The ${label} feed` : `${singles.length} ${label} ${singles.length === 1 ? "post" : "posts"}`}, live
          from {label}. Previews load from their own servers, which may set cookies.
        </p>
        <Button type="button" onClick={allowPreviews}>
          Load live previews
        </Button>
        {outLink && (
          <a
            href={outLink}
            target="_blank"
            rel="noreferrer noopener"
            className="font-mono text-[10px] uppercase tracking-widest text-brand-ink/60 underline-offset-4 hover:text-brand-accent hover:underline"
          >
            Or open it on {label}
          </a>
        )}
      </div>
    );
  }

  return (
    <div className="space-y-6">
      {profiles.map((post) =>
        embedUrl(post) ? (
          <div key={post.url} className="mx-auto max-w-xl">
            <Frame post={post} height={META[platform].profile} />
          </div>
        ) : (
          <ProfileLink key={post.url} post={post} />
        ),
      )}
      {singles.length > 0 && (
        <div className="grid gap-4 sm:grid-cols-2 2xl:grid-cols-3">
          {singles.map((post) => (
            <Frame key={post.url} post={post} height={META[platform].post} />
          ))}
        </div>
      )}
    </div>
  );
};
